import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiError, BACKEND_URL } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import MvpLeaderboard from "../components/MvpLeaderboard";
import PreparePostDialog from "../components/PreparePostDialog";
import GroupAssignDialog from "../components/GroupAssignDialog";
import ManageGroupDialog from "../components/ManageGroupDialog";
import EditedUploadDialog from "../components/EditedUploadDialog";
import StaffCoverButton from "../components/StaffCoverButton";
import ClientDiagnosticDialog from "../components/ClientDiagnosticDialog";
import { useAuth } from "../context/AuthContext";
import {
  Hammer,
  Megaphone,
  Video,
  ExternalLink,
  Store,
  Pencil,
  Sparkles,
  Layers,
  Upload,
  ChevronRight,
  CheckCircle2,
  X,
  Trash2,
  Clock,
  Settings2,
  Stethoscope,
  Smartphone,
  Unlink,
  Scissors,
  ArrowUpRight,
  Search,
} from "lucide-react";

// Refactored subcomponents (P1, Feb 2026): everything that used to be
// defined inline at the bottom of this file now lives under
// `./production/*` for readability and to keep this file focused on
// state + top-level layout.
import {
  TABS,
  KIND_FILTERS,
  countCards,
  countByKind,
  formatGroupCreatedAt,
  formatActivity,
  lastContentBadge,
  lastEditedBadge,
} from "./production/utils";
import { Thumb, iconFor } from "./production/Thumb";
import { KpiCard } from "./production/KpiCard";
import { MiniToggle } from "./production/MiniToggle";
import { ClientColorChip } from "./production/ClientColorChip";
import { NoteDialog } from "./production/NoteDialog";
import { DirectUploadButton, MusicUploadButton, UploadLinkButton } from "./production/ClientActions";
import { GroupZipButton } from "./production/GroupZipButton";
import { SingleDownloadButton } from "./production/SingleDownloadButton";


export default function ProductionDashboard() {
  const [tab, setTab] = useState("todo");
  const [kind, setKind] = useState("");
  const [items, setItems] = useState([]);
  const [allProClients, setAllProClients] = useState([]);
  const [counts, setCounts] = useState({
    todo: { groups: 0, singles: 0, total: 0 },
    edited: { groups: 0, singles: 0, total: 0 },
    published: { groups: 0, singles: 0, total: 0 },
    total: { groups: 0, singles: 0, total: 0 },
  });
  const [loading, setLoading] = useState(false);
  const [workingId, setWorkingId] = useState(null);
  const [noteEditing, setNoteEditing] = useState(null); // {id, note, clientName, fileName}
  const [preparing, setPreparing] = useState(null); // {clientId, clientName, mediaUrl, mediaKind, sourceMessageId}
  const [selection, setSelection] = useState({}); // {msgId: clientId}
  const [groupDialog, setGroupDialog] = useState(null); // {messageIds, existingName, existingColor}
  const [uploadTarget, setUploadTarget] = useState(null); // {messageId, fileName, clientName, existing}
  const [manageGroup, setManageGroup] = useState(null); // {groupId, groupName, clientId, items}
  const [diagTarget, setDiagTarget] = useState(null); // {clientId, clientName}
  const [clientSearch, setClientSearch] = useState("");
  const { user: currentUser } = useAuth();
  const isAdmin = currentUser?.role === "admin";
  // Per-session collapse map. ALL clients are collapsed on first load
  // (user requirement: "parta da chiusa"). Closing in one tab also
  // collapses in the other tabs because the state lives on the parent.
  const [expandedClients, setExpandedClients] = useState(() => new Set());
  // Track the live colour tag locally so the chip flips immediately on
  // pick without waiting for the next /staff/tasks refresh.
  const [colorTags, setColorTags] = useState({}); // { clientId: "#hex" | null }

  const toggleClient = useCallback((clientId) => {
    if (!clientId) return;
    setExpandedClients((prev) => {
      const next = new Set(prev);
      if (next.has(clientId)) next.delete(clientId);
      else next.add(clientId);
      return next;
    });
  }, []);

  const setClientColor = useCallback(async (clientId, color) => {
    if (!clientId) return;
    // Optimistic update — revert on failure.
    setColorTags((prev) => ({ ...prev, [clientId]: color }));
    try {
      await api.put(`/staff/clients/${clientId}/color-tag`, { color });
    } catch (e) {
      toast.error(apiError(e));
      setColorTags((prev) => ({ ...prev, [clientId]: null }));
    }
  }, []);

  // Hook used by DirectUploadButton: when an upload completes, immediately open
  // the existing PreparePostDialog with the newly-created message id.
  const setDirectUploaded = useCallback((payload) => {
    if (!payload || !payload.client?.id) return;
    setPreparing({
      clientId: payload.client.id,
      clientName: payload.client.name_salone || "",
      mediaUrl:
        payload.media_url ||
        `/api/files/dropbox?path=${encodeURIComponent(payload.dropbox_path || "")}`,
      mediaKind: payload.media_kind || "video",
      sourceMessageId: payload.message_id,
    });
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/staff/tasks`, {
        params: { status: tab, ...(kind ? { kind } : {}) },
      });
      setItems(data.items || []);
      setCounts(data.counts || {});
      setAllProClients(data.all_pro_clients || []);
      // Hydrate colour tags from the response so they survive tab switches.
      setColorTags((prev) => {
        const next = { ...prev };
        for (const it of data.items || []) {
          const cid = it.client?.id;
          if (cid && !(cid in next)) next[cid] = it.client.color_tag || null;
        }
        for (const c of data.all_pro_clients || []) {
          if (c.id && !(c.id in next)) next[c.id] = c.color_tag || null;
        }
        return next;
      });
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, [tab, kind]);

  useEffect(() => {
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [load]);

  const flag = async (msg, field, next) => {
    setWorkingId(msg.id);
    try {
      const body = {};
      body[field] = next;
      await api.post(`/staff/tasks/${msg.id}`, body);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setWorkingId(null);
    }
  };

  const flagGroup = async (groupId, field, next) => {
    setWorkingId(`group-${groupId}`);
    try {
      const body = {};
      body[field] = next;
      await api.post(`/staff/groups/${groupId}/flags`, body);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setWorkingId(null);
    }
  };

  const saveNote = async (messageId, note) => {
    try {
      await api.patch(`/staff/tasks/${messageId}/note`, { note });
      toast.success(note ? "Nota salvata" : "Nota rimossa");
      setNoteEditing(null);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const saveGroupNote = async (groupId, note) => {
    try {
      await api.patch(`/staff/groups/${groupId}/note`, { note });
      toast.success(note ? "Nota del gruppo salvata" : "Nota rimossa dal gruppo");
      setNoteEditing(null);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const renameGroup = async (gid, currentName) => {
    const next = window.prompt(
      "Rinomina il gruppo (max 80 caratteri):",
      currentName || ""
    );
    if (next === null) return; // cancelled
    const trimmed = next.trim();
    if (!trimmed) {
      toast.error("Il nome non può essere vuoto");
      return;
    }
    if (trimmed === (currentName || "").trim()) return; // no change
    try {
      await api.patch(`/staff/groups/${gid}/name`, { name: trimmed });
      toast.success("Gruppo rinominato");
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const deleteCard = async (m, fileName) => {
    if (!window.confirm(
      `Eliminare definitivamente "${fileName}"?\n\nIl contenuto sparisce dalla produzione e dalla chat con il cliente. Il file caricato su Dropbox resta disponibile.\n\nQuesta azione NON è reversibile.`
    )) return;
    setWorkingId(m.id);
    try {
      await api.delete(`/staff/tasks/${m.id}`);
      toast.success("Contenuto eliminato");
      // remove from selection if present
      setSelection((prev) => {
        const next = { ...prev };
        delete next[m.id];
        return next;
      });
      await load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setWorkingId(null);
    }
  };

  const deleteGroup = async (groupId, groupName, count) => {
    if (!groupId) return;
    const label = groupName || "questo gruppo";
    if (!window.confirm(
      `Eliminare il gruppo "${label}"?\n\nVerranno rimossi ${count} contenut${count === 1 ? "o" : "i"} dalla produzione e dalla chat con il cliente. I file caricati su Dropbox restano disponibili.\n\nQuesta azione NON è reversibile.`
    )) return;
    setWorkingId(groupId);
    try {
      await api.delete(`/staff/groups/${groupId}`);
      toast.success("Gruppo eliminato");
      // de-select any of its members that were ticked
      setSelection((prev) => {
        const next = { ...prev };
        for (const k of Object.keys(next)) {
          // Selection map is keyed by message_id, not group_id, so a
          // simple full reload of the page state is the cleanest way to
          // forget any selections from members we just deleted.
          delete next[k];
        }
        return next;
      });
      await load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setWorkingId(null);
    }
  };

  // Sciogli gruppo: rimuove il gruppo mantenendo i singoli contenuti in
  // produzione (diventano card singole non raggruppate). Contrasto con
  // deleteGroup che invece elimina i contenuti stessi.
  const dissolveGroup = async (groupId, groupName, count) => {
    if (!groupId) return;
    const label = groupName || "questo gruppo";
    if (!window.confirm(
      `Sciogliere il gruppo "${label}"?\n\nI ${count} contenut${count === 1 ? "o" : "i"} tornano nella produzione come card singole. Il gruppo scompare ma i video/foto restano al loro posto.\n\nQuesta azione può essere annullata rifacendo il gruppo.`
    )) return;
    setWorkingId(groupId);
    try {
      const messageIds = items
        .filter((m) => m.group_id === groupId)
        .map((m) => m.id);
      if (messageIds.length === 0) {
        toast.error("Nessun contenuto da sciogliere");
        return;
      }
      await api.post("/staff/groups/unassign", { message_ids: messageIds });
      toast.success("Gruppo sciolto");
      // Deseleziona ogni membro del gruppo eventualmente selezionato.
      setSelection((prev) => {
        const next = { ...prev };
        for (const id of messageIds) delete next[id];
        return next;
      });
      await load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setWorkingId(null);
    }
  };

  const toggleSelect = (msg) => {
    const clientId = msg.client?.id || "x";
    setSelection((prev) => {
      const next = { ...prev };
      if (next[msg.id]) {
        delete next[msg.id];
      } else {
        // Enforce single-client selection: if switching client, reset
        const otherClients = Object.values(next).filter((c) => c !== clientId);
        if (otherClients.length > 0) {
          toast.info("Puoi raggruppare solo contenuti dello stesso cliente");
          return { [msg.id]: clientId };
        }
        next[msg.id] = clientId;
      }
      return next;
    });
  };

  const selectedIds = Object.keys(selection);
  const selectedCount = selectedIds.length;

  const clearSelection = () => setSelection({});

  const openGroupDialog = () => {
    if (selectedCount < 2) {
      toast.error("Seleziona almeno 2 contenuti");
      return;
    }
    // Pre-fill if selected items already share a group
    const selectedItems = items.filter((m) => selection[m.id]);
    const firstGroup = selectedItems.find((m) => m.group_id);
    setGroupDialog({
      messageIds: selectedIds,
      existingName: firstGroup?.group_name || "",
      existingColor: firstGroup?.group_color || "",
    });
  };

  const ungroupSelected = async () => {
    try {
      await api.post("/staff/groups/unassign", {
        message_ids: selectedIds,
      });
      toast.success("Contenuti tolti dal gruppo");
      clearSelection();
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  // NOTE: The Postpickr button is now a native <a target="postpickr_tab"> — the
  // browser is better at reusing the named tab than a JS window.open() call
  // from inside an iframe.

  // Group items by client
  const grouped = useMemo(() => {
    const g = new Map();
    for (const m of items) {
      const cid = m.client?.id || "sconosciuto";
      if (!g.has(cid)) g.set(cid, { client: m.client, items: [] });
      g.get(cid).items.push(m);
    }
    // Sort policy:
    //   - tab "todo" / "edited": cronologico ASC (più vecchio in alto)
    //   - tab "published":      cronologico DESC su task_published_at
    //
    // Per i gruppi (più video raggruppati): tutti gli item dello stesso
    // group_id stanno contigui; la posizione del gruppo è determinata
    // dall'item più vecchio (todo/edited) o più recente pubblicato (published).
    const sortVal = (m) => {
      if (tab === "published") {
        const ts = m.task_published_at || m.created_at;
        return ts ? -new Date(ts).getTime() : 0;
      }
      return m.created_at ? new Date(m.created_at).getTime() : 0;
    };
    for (const entry of g.values()) {
      // Precompute group anchor = best sortVal among items of the same group.
      const groupAnchor = new Map();
      for (const m of entry.items) {
        if (!m.group_id) continue;
        const v = sortVal(m);
        const cur = groupAnchor.get(m.group_id);
        if (cur === undefined || v < cur) groupAnchor.set(m.group_id, v);
      }
      entry.items.sort((a, b) => {
        const ka = a.group_id ? groupAnchor.get(a.group_id) : sortVal(a);
        const kb = b.group_id ? groupAnchor.get(b.group_id) : sortVal(b);
        if (ka !== kb) return ka - kb;
        // v1.40 — inside the SAME group, honour the client-chosen order
        // from the Magic Link picker (clip_sequence) so the montage team
        // sees clips in the sequence the client intended, not by upload
        // time. Fall back to cronologico ASC when no sequence is set.
        if (a.group_id && a.group_id === b.group_id) {
          const sa = a.clip_sequence;
          const sb = b.clip_sequence;
          if (sa != null && sb != null && sa !== sb) return sa - sb;
          if (sa != null && sb == null) return -1;
          if (sa == null && sb != null) return 1;
        }
        const ca = a.created_at ? new Date(a.created_at).getTime() : 0;
        const cb = b.created_at ? new Date(b.created_at).getTime() : 0;
        return ca - cb;
      });
    }
    // On the "Da lavorare" tab, also surface ALL approved Pro clients
    // (even those without content yet) so the staff sees their card and
    // can use "Carica video diretto" before any Telegram upload arrives.
    // Skipped on Lavorati/Pubblicati to avoid noise.
    if (tab === "todo") {
      for (const c of allProClients) {
        if (!c?.id || g.has(c.id)) continue;
        g.set(c.id, { client: c, items: [] });
      }
    }
    // Sort clients: those with content first (cronologico), then empty ones alphabetically.
    const arr = Array.from(g.values());
    arr.sort((a, b) => {
      const aEmpty = a.items.length === 0;
      const bEmpty = b.items.length === 0;
      if (aEmpty !== bEmpty) return aEmpty ? 1 : -1;
      if (aEmpty && bEmpty) {
        return (a.client?.name_salone || "").localeCompare(b.client?.name_salone || "", "it");
      }
      return 0;
    });
    return arr;
  }, [items, tab, allProClients]);

  // Client-side filter: cerca per nome cliente (case/accent-insensitive).
  // Applicato dopo il grouping così funziona in tutte e tre le tab
  // (Da controllare / Da montare / Pubblicati) senza toccare la rete.
  const filteredGrouped = useMemo(() => {
    const q = clientSearch.trim().toLowerCase();
    if (!q) return grouped;
    const norm = (s) =>
      (s || "")
        .toString()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase();
    const nq = norm(q);
    return grouped.filter((g) => norm(g.client?.name_salone).includes(nq));
  }, [grouped, clientSearch]);

  const openDropboxFolder = async (clientId, subpath) => {
    // Open blank tab synchronously (popup-blocker friendly), then redirect it
    const w = window.open("about:blank", "dropbox_tab");
    try {
      const { data } = await api.get(
        `/staff/clients/${clientId}/dropbox-folder`,
        { params: subpath ? { subpath } : {} }
      );
      if (w && data?.url) {
        w.location.href = data.url;
      } else if (data?.url) {
        window.open(data.url, "dropbox_tab");
      }
    } catch (e) {
      if (w) w.close();
      toast.error(apiError(e));
    }
  };

  const renderCard = (m, client) => {
    const a = m.attachment || {};
    const rel = a.dropbox_url || a.url;
    const src = rel ? `${BACKEND_URL}${rel}` : null;
    const fileName =
      a.display_name || a.file_name || a.kind || "file";
    const edited = !!m.task_edited;
    const published = !!m.task_published;
    const inProgress = !!m.task_in_progress;
    const isSelected = !!selection[m.id];
    // v1.40 — client-chosen sequence & label from Magic Link picker.
    const seq = m.clip_sequence;
    const clipLabel = (m.clip_label || "").trim();
    return (
      <div
        key={m.id}
        data-testid={`prod-card-${m.id}`}
        className={`rounded-xl border overflow-hidden flex flex-row transition-colors bg-white ${
          isSelected
            ? "border-[var(--mp-ink)] ring-2 ring-[var(--mp-ink)] ring-offset-1"
            : "border-[var(--mp-border)]"
        }`}
      >
        <label
          className="shrink-0 w-6 grid place-items-center cursor-pointer bg-[var(--mp-dash-bg)] border-r border-[var(--mp-border)]"
          title={isSelected ? "Deseleziona" : "Seleziona"}
        >
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => toggleSelect(m)}
            data-testid={`prod-select-${m.id}`}
            className="h-3.5 w-3.5 accent-[var(--mp-ink)]"
          />
        </label>
        <a
          href={src || "#"}
          target="_blank"
          rel="noreferrer"
          onClick={(e) => !src && e.preventDefault()}
          className="block shrink-0 w-24 sm:w-28 aspect-square bg-[var(--mp-dash-bg)] relative group"
          title={fileName}
        >
          <Thumb a={a} />
          {a.kind === "video" && (
            <div className="absolute inset-0 grid place-items-center pointer-events-none">
              <div className="h-8 w-8 rounded-full bg-white/90 grid place-items-center border border-[var(--mp-ink)]">
                <Video className="h-3.5 w-3.5" />
              </div>
            </div>
          )}
          {src && (
            <div className="absolute top-1.5 right-1.5 h-6 w-6 rounded-full bg-white/95 border border-[var(--mp-ink)] grid place-items-center opacity-0 group-hover:opacity-100 transition-opacity">
              <ExternalLink className="h-3 w-3" />
            </div>
          )}
          {(edited || published) && (
            <div className="absolute bottom-1.5 left-1.5 flex gap-1">
              {edited && (
                <div
                  className="h-5 w-5 rounded-full bg-[var(--mp-yellow)] border border-[var(--mp-ink)] grid place-items-center"
                  title="Lavorato"
                >
                  <Hammer className="h-2.5 w-2.5" />
                </div>
              )}
              {published && (
                <div
                  className="h-5 w-5 rounded-full bg-[var(--mp-ink)] text-white grid place-items-center"
                  title="Pubblicato"
                >
                  <Megaphone className="h-2.5 w-2.5" />
                </div>
              )}
            </div>
          )}
        </a>
        <div className="px-2.5 py-2 flex flex-col gap-1.5 flex-1 min-w-0">
          {(clipLabel || seq != null) && (
            <div
              data-testid={`prod-card-clip-meta-${m.id}`}
              className="flex items-center gap-1.5 min-w-0"
            >
              {seq != null && (
                <span
                  data-testid={`prod-card-clip-seq-${m.id}`}
                  title={`Ordine scelto dal cliente: ${seq}`}
                  className="shrink-0 h-5 min-w-[22px] px-1.5 rounded-full bg-[var(--mp-yellow)] border border-[var(--mp-ink)] text-[var(--mp-ink)] text-[10px] font-bold grid place-items-center"
                >
                  {String(seq).padStart(2, "0")}
                </span>
              )}
              {clipLabel && (
                <span
                  data-testid={`prod-card-clip-label-${m.id}`}
                  title={clipLabel}
                  className="text-[12px] font-semibold text-[var(--mp-ink)] truncate"
                >
                  {clipLabel}
                </span>
              )}
            </div>
          )}
          <div className="flex items-center gap-1 text-[11px] text-[var(--mp-muted)] min-w-0">
            <span className="shrink-0">{iconFor(a.kind)}</span>
            <span className="truncate" title={fileName}>
              {fileName}
            </span>
          </div>
          <div className="text-[9px] uppercase tracking-[0.14em] text-[var(--mp-muted)] truncate">
            {new Date(m.created_at).toLocaleDateString("it-IT", {
              day: "2-digit",
              month: "2-digit",
            })}
            {" · "}
            {new Date(m.created_at).toLocaleTimeString("it-IT", {
              hour: "2-digit",
              minute: "2-digit",
            })}
          </div>
          <div className="mt-auto flex flex-wrap gap-1 pt-1">
            <MiniToggle
              checked={inProgress}
              onClick={() => flag(m, "in_progress", !inProgress)}
              loading={workingId === m.id}
              tone="blue"
              icon={<Clock className="h-3 w-3 shrink-0" />}
              label="In lavorazione"
              title={
                inProgress
                  ? formatActivity("In lavorazione", m.task_in_progress_at, m.task_in_progress_by)
                  : "Segna in lavorazione"
              }
              testId={`prod-toggle-inprogress-${m.id}`}
            />
            <MiniToggle
              checked={edited}
              onClick={() => flag(m, "edited", !edited)}
              loading={workingId === m.id}
              tone="yellow"
              icon={<Hammer className="h-3 w-3 shrink-0" />}
              label="Da montare a mano"
              title={
                edited
                  ? formatActivity("Da montare a mano", m.task_edited_at, m.task_edited_by)
                  : "Segna da montare a mano"
              }
              testId={`prod-toggle-edited-${m.id}`}
            />
            <MiniToggle
              checked={published}
              onClick={() => flag(m, "published", !published)}
              loading={workingId === m.id}
              tone="ink"
              icon={<Megaphone className="h-3 w-3 shrink-0" />}
              label="Pubblicato"
              title={
                published
                  ? formatActivity(
                      "Pubblicato",
                      m.task_published_at,
                      m.task_published_by
                    )
                  : "Segna pubblicato"
              }
              testId={`prod-toggle-published-${m.id}`}
            />
            <button
              type="button"
              onClick={() =>
                setNoteEditing({
                  id: m.id,
                  note: m.task_note || "",
                  clientName: client?.name_salone || "",
                  fileName,
                })
              }
              data-testid={`prod-note-btn-${m.id}`}
              title={m.task_note ? "Modifica nota" : "Aggiungi nota"}
              className={`flex-1 h-7 px-2 rounded-full border inline-flex items-center justify-center gap-1 transition-colors text-[11px] font-medium whitespace-nowrap ${
                m.task_note
                  ? "bg-[var(--mp-yellow)] border-[var(--mp-ink)] text-[var(--mp-ink)]"
                  : "bg-white border-[var(--mp-border)] text-[var(--mp-muted)] hover:text-[var(--mp-ink)] hover:border-[var(--mp-ink)]"
              }`}
            >
              <Pencil className="h-3 w-3 shrink-0" />
              <span>Nota</span>
            </button>
            {a.dropbox_path && (
              <SingleDownloadButton messageId={m.id} fileName={fileName} />
            )}
            <button
              type="button"
              onClick={() =>
                setPreparing({
                  clientId: client?.id,
                  clientName: client?.name_salone || "",
                  mediaUrl:
                    m.edited_asset?.dropbox_url ||
                    m.edited_asset?.url ||
                    a.dropbox_url ||
                    a.url ||
                    "",
                  mediaKind: a.kind === "photo" ? "foto" : "video",
                  sourceMessageId: m.id,
                })
              }
              data-testid={`prod-prep-btn-${m.id}`}
              title="Programma post (apri dialog AI + Coda Metricool)"
              className="flex-1 h-7 px-2 rounded-full border border-[var(--mp-ink)] bg-[var(--mp-yellow)] text-[var(--mp-ink)] inline-flex items-center justify-center gap-1 hover:bg-[var(--mp-ink)] hover:text-white transition-colors text-[11px] font-medium whitespace-nowrap"
            >
              <Sparkles className="h-3 w-3 shrink-0" />
              <span>Programma</span>
            </button>
            <button
              type="button"
              onClick={() => deleteCard(m, fileName)}
              disabled={workingId === m.id}
              data-testid={`prod-delete-btn-${m.id}`}
              title="Elimina contenuto"
              className="shrink-0 h-7 w-7 rounded-full border border-[var(--mp-border)] bg-white text-red-600 inline-flex items-center justify-center hover:bg-red-50 hover:border-red-300 transition-colors disabled:opacity-50"
            >
              <Trash2 className="h-3 w-3" />
            </button>
          </div>
          {m.task_note && (
            <div
              data-testid={`prod-note-preview-${m.id}`}
              className="mt-1 text-[10px] text-[var(--mp-muted)] italic line-clamp-2 whitespace-pre-wrap border-t border-[var(--mp-border)] pt-1"
            >
              📌 {m.task_note}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div data-testid="production-dashboard" className="space-y-8 fade-up">
      <div>
        <div className="text-[10px] font-bold uppercase tracking-[0.24em] text-[var(--mp-muted)]">
          Produzione · Team agenzia
        </div>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tighter mt-2">
          Contenuti in lavorazione
        </h1>
        <p className="mt-3 text-[var(--mp-muted)] max-w-2xl">
          Tutti i contenuti ricevuti dai saloni, raggruppati per cliente. Marca come{" "}
          <strong>lavorato</strong> quando hai finito di montarli e come <strong>pubblicato</strong>{" "}
          quando vanno online.
        </p>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-3 gap-4">
        <KpiCard
          label="Da controllare"
          value={counts.todo?.total ?? 0}
          hint={
            counts.todo?.singles
              ? `${counts.todo.groups} gruppi · ${counts.todo.singles} singoli`
              : `${counts.todo?.groups ?? 0} gruppi`
          }
          tone="yellow"
          testId="kpi-todo"
        />
        <KpiCard
          label="Pronti da pubblicare"
          value={counts.edited?.total ?? 0}
          hint={`${counts.edited?.groups ?? 0} gruppi`}
          tone="paper"
          testId="kpi-edited"
        />
        <KpiCard
          label="Già pubblicati"
          value={counts.published?.total ?? 0}
          hint={`${counts.published?.groups ?? 0} gruppi`}
          tone="ink"
          testId="kpi-published"
        />
      </div>

      {/* MVP della settimana */}
      <MvpLeaderboard days={7} />

      {/* External montage tool — big yellow CTA between the leaderboard
          and the per-client production list. Opens in a new tab so
          Produzione stays open in the background. */}
      <a
        data-testid="prod-montatore-cta"
        href="https://haircut-cuts.emergent.host/"
        target="_blank"
        rel="noopener noreferrer"
        className="block rounded-3xl border-2 border-[var(--mp-ink)] bg-[var(--mp-yellow)] px-6 py-6 sm:py-8 shadow-brutal-sm hover:-translate-y-0.5 hover:shadow-brutal-md active:translate-y-0 transition-transform"
      >
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 rounded-2xl bg-[var(--mp-ink)] text-[var(--mp-yellow)] grid place-items-center shrink-0">
              <Scissors className="h-6 w-6" />
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--mp-ink)]/70">
                Strumento esterno
              </div>
              <div className="font-display text-3xl sm:text-4xl leading-none mt-0.5">
                Montatore
              </div>
            </div>
          </div>
          <div className="inline-flex items-center gap-1.5 text-sm font-bold text-[var(--mp-ink)]">
            Apri
            <ArrowUpRight className="h-4 w-4" />
          </div>
        </div>
      </a>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--mp-muted)] pointer-events-none" />
          <input
            type="search"
            value={clientSearch}
            onChange={(e) => setClientSearch(e.target.value)}
            placeholder="Cerca cliente…"
            data-testid="prod-client-search"
            className="w-full h-10 pl-10 pr-9 rounded-full bg-white border border-[var(--mp-border)] text-sm placeholder:text-[var(--mp-muted)] focus:outline-none focus:border-[var(--mp-ink)] focus:ring-1 focus:ring-[var(--mp-ink)]"
          />
          {clientSearch && (
            <button
              type="button"
              onClick={() => setClientSearch("")}
              data-testid="prod-client-search-clear"
              title="Pulisci ricerca"
              className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 rounded-full grid place-items-center text-[var(--mp-muted)] hover:bg-[var(--mp-dash-bg)] hover:text-[var(--mp-ink)]"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        <div className="flex items-center gap-1 rounded-full bg-white border border-[var(--mp-border)] p-1">
          {KIND_FILTERS.map((k) => (
            <button
              key={k.value}
              data-testid={`prod-kind-${k.value || "all"}`}
              onClick={() => setKind(k.value)}
              className={`px-3 h-8 rounded-full text-xs font-medium transition-colors ${
                kind === k.value
                  ? "bg-[var(--mp-yellow)] text-[var(--mp-ink)]"
                  : "text-[var(--mp-muted)] hover:text-[var(--mp-ink)]"
              }`}
            >
              {k.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 rounded-full bg-white border border-[var(--mp-border)] p-1 max-w-full overflow-x-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
          {TABS.map((t) => (
            <button
              key={t.value}
              data-testid={`prod-tab-${t.value}`}
              onClick={() => setTab(t.value)}
              className={`shrink-0 px-2.5 sm:px-4 h-8 rounded-full text-sm font-medium transition-colors inline-flex items-center gap-1.5 ${
                tab === t.value
                  ? "bg-[var(--mp-ink)] text-white"
                  : "text-[var(--mp-muted)] hover:text-[var(--mp-ink)]"
              }`}
            >
              {t.label}
              <span
                className={`inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold ${
                  tab === t.value
                    ? "bg-white/20 text-white"
                    : "bg-[var(--mp-dash-bg)] text-[var(--mp-muted)]"
                }`}
              >
                {counts[t.value]?.groups ?? 0}
              </span>
              {(counts[t.value]?.singles ?? 0) > 0 && (
                <span
                  className={`text-[10px] font-medium ${
                    tab === t.value ? "text-white/70" : "text-[var(--mp-muted)]"
                  }`}
                  title="Video singoli (non raggruppati)"
                >
                  +{counts[t.value].singles}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Selection toolbar */}
      {selectedCount > 0 && (
        <div
          data-testid="prod-selection-bar"
          className="sticky top-2 z-20 rounded-full bg-[var(--mp-ink)] text-white px-4 py-2 flex items-center gap-3 shadow-brutal-sm flex-wrap"
        >
          <div className="text-sm font-medium">
            {selectedCount} selezionato{selectedCount === 1 ? "" : "i"}
          </div>
          <div className="flex items-center gap-2 ml-auto">
            <Button
              size="sm"
              onClick={openGroupDialog}
              data-testid="prod-group-btn"
              disabled={selectedCount < 2}
              className="rounded-full bg-[var(--mp-yellow)] hover:bg-[var(--mp-yellow)] text-[var(--mp-ink)] border border-[var(--mp-ink)] disabled:opacity-60"
            >
              <Layers className="h-4 w-4 mr-1" /> Raggruppa
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={ungroupSelected}
              data-testid="prod-ungroup-btn"
              className="rounded-full border-white text-white hover:bg-white/10"
            >
              Rimuovi gruppo
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={clearSelection}
              data-testid="prod-selection-clear"
              className="rounded-full text-white hover:bg-white/10"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {loading && items.length === 0 && grouped.length === 0 ? (
        <div className="p-10 text-center text-[var(--mp-muted)]">Caricamento…</div>
      ) : filteredGrouped.length === 0 ? (
        <div className="rounded-3xl border border-dashed border-[var(--mp-border)] bg-white p-10 text-center text-[var(--mp-muted)]">
          {clientSearch.trim()
            ? `Nessun cliente trovato per "${clientSearch.trim()}".`
            : tab === "todo"
            ? "Nessun contenuto da controllare in questo momento."
            : tab === "edited"
            ? "Nessun contenuto pronto per la pubblicazione."
            : "Nessun contenuto ancora pubblicato."}
        </div>
      ) : (
        <div className="space-y-8">
          {filteredGrouped.map((group) => {
            const cid = group.client?.id;
            const isExpanded = cid ? expandedClients.has(cid) : true;
            const tagColor = cid ? colorTags[cid] : null;
            return (
            <section key={cid || "x"} className="space-y-4">
              <div className="flex items-center gap-3 flex-wrap sm:flex-nowrap">
                {/* LEFT: chevron + colour chip + client name */}
                <div className="flex items-center gap-2.5 min-w-0 flex-1">
                  <button
                    type="button"
                    onClick={() => toggleClient(cid)}
                    data-testid={`prod-client-toggle-${cid}`}
                    title={isExpanded ? "Comprimi cliente" : "Espandi cliente"}
                    className="shrink-0 h-8 w-8 rounded-full grid place-items-center hover:bg-[var(--mp-dash-bg)] transition-colors"
                  >
                    <ChevronRight
                      className={`h-4 w-4 transition-transform ${
                        isExpanded ? "rotate-90" : ""
                      }`}
                    />
                  </button>
                  {cid ? (
                    <ClientColorChip
                      clientId={cid}
                      color={tagColor}
                      onChange={(c) => setClientColor(cid, c)}
                    />
                  ) : (
                    <div className="shrink-0 h-8 w-8 rounded-full bg-[var(--mp-yellow)] grid place-items-center border border-[var(--mp-ink)]">
                      <Store className="h-4 w-4" />
                    </div>
                  )}
                  <Link
                    to={`/dashboard/clienti/${cid}`}
                    data-testid={`prod-client-header-${cid}`}
                    className="leading-tight group min-w-0"
                  >
                    <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[var(--mp-muted)]">
                      Cliente
                    </div>
                    <div className="font-display text-lg tracking-tight group-hover:underline flex items-center gap-2 flex-wrap">
                      <span className="truncate">{group.client?.name_salone || "—"}</span>
                      {(() => {
                        const { videos, photos } = countByKind(group.items);
                        if (videos === 0 && photos === 0) return null;
                        return (
                          <span
                            data-testid={`prod-content-dot-${cid}`}
                            className="inline-flex items-center gap-1.5"
                            title={
                              `${videos} video da lavorare` +
                              (photos > 0 ? ` · ${photos} foto` : "")
                            }
                          >
                            {videos > 0 && (
                              <span className="inline-flex items-center gap-0.5 text-[11px] font-bold text-[var(--mp-primary)] normal-case">
                                <span className="inline-block h-2.5 w-2.5 rounded-full bg-[var(--mp-primary)]" />
                                {videos}
                              </span>
                            )}
                            {photos > 0 && (
                              <span className="inline-flex items-center gap-0.5 text-[11px] font-bold text-[#0061FF] normal-case">
                                <span className="inline-block h-2.5 w-2.5 rounded-full bg-[#0061FF]" />
                                {photos}
                              </span>
                            )}
                          </span>
                        );
                      })()}
                    </div>
                  </Link>
                </div>

                {/* RIGHT: info badges + action bar (always aligned right on desktop, wraps below on mobile) */}
                <div className="flex items-center gap-2 flex-wrap justify-start sm:justify-end w-full sm:w-auto sm:shrink-0">
                  {/* Info badges cluster */}
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {(() => {
                      const b = lastContentBadge(group.client?.last_content_at);
                      const tones = {
                        muted: "bg-[var(--mp-dash-bg)] text-[var(--mp-muted)] border-[var(--mp-border)]",
                        ok: "bg-green-50 text-green-800 border-green-200",
                        warn: "bg-amber-50 text-amber-800 border-amber-200",
                        danger: "bg-red-50 text-red-800 border-red-200",
                      };
                      return (
                        <span
                          data-testid={`prod-last-content-${cid}`}
                          title={
                            group.client?.last_content_at
                              ? `Ultimo contenuto ricevuto il ${new Date(
                                  group.client.last_content_at
                                ).toLocaleString("it-IT")}`
                              : "Questo cliente non ha mai inviato contenuti via Telegram"
                          }
                          className={`inline-flex items-center h-7 px-2.5 rounded-full border text-[10px] font-bold uppercase tracking-[0.15em] ${
                            tones[b.tone] || tones.muted
                          }`}
                        >
                          {b.label}
                        </span>
                      );
                    })()}
                    {(() => {
                      const b = lastEditedBadge(group.client?.last_edited_at);
                      const tones = {
                        muted: "bg-[var(--mp-dash-bg)] text-[var(--mp-muted)] border-[var(--mp-border)]",
                        done: "bg-[var(--mp-yellow)] text-[var(--mp-ink)] border-[var(--mp-ink)]",
                      };
                      return (
                        <span
                          data-testid={`prod-last-edited-${cid}`}
                          title={
                            group.client?.last_edited_at
                              ? `Ultimo "Lavorato" segnato il ${new Date(
                                  group.client.last_edited_at
                                ).toLocaleString("it-IT")}`
                              : "Nessun contenuto ancora segnato come Lavorato per questo cliente"
                          }
                          className={`inline-flex items-center h-7 px-2.5 rounded-full border text-[10px] font-bold uppercase tracking-[0.15em] ${
                            tones[b.tone] || tones.muted
                          }`}
                        >
                          {b.label}
                        </span>
                      );
                    })()}
                    <span
                      data-testid={`prod-video-count-${cid}`}
                      className="inline-flex items-center h-7 px-2.5 rounded-full border border-[var(--mp-border)] bg-white text-[10px] font-bold uppercase tracking-[0.15em] text-[var(--mp-muted)]"
                      title="Numero di card in produzione"
                    >
                      {countCards(group.items)} video
                    </span>
                  </div>

                  {/* Vertical divider between info and actions */}
                  {group.client?.id && (
                    <div className="hidden md:block h-6 w-px bg-[var(--mp-border)] mx-0.5" />
                  )}

                  {/* Action bar: icon-only buttons, always same width */}
                  {group.client?.id && (
                    <div
                      data-testid={`prod-actionbar-${group.client.id}`}
                      className="inline-flex items-center gap-1 rounded-full bg-white border border-[var(--mp-border)] p-1 shrink-0"
                    >
                      <DirectUploadButton
                        clientId={group.client.id}
                        clientName={group.client.name_salone}
                        onUploaded={(payload) => setDirectUploaded(payload)}
                      />
                      <MusicUploadButton
                        clientId={group.client.id}
                        clientName={group.client.name_salone}
                      />
                      <UploadLinkButton
                        clientId={group.client.id}
                        clientName={group.client.name_salone}
                      />
                      <StaffCoverButton
                        clientId={group.client.id}
                        clientName={group.client.name_salone}
                      />
                      <button
                        type="button"
                        onClick={() => openDropboxFolder(group.client.id)}
                        data-testid={`prod-dropbox-btn-${group.client?.id}`}
                        title="Apri la cartella Dropbox del cliente"
                        className="h-8 w-8 rounded-full bg-[#0061FF] text-white grid place-items-center hover:bg-[#0052D4] transition-colors"
                      >
                        <svg
                          viewBox="0 0 24 24"
                          className="h-3.5 w-3.5"
                          fill="currentColor"
                          aria-hidden="true"
                        >
                          <path d="M6 2l6 3.75L6 9.5 0 5.75 6 2zm12 0l6 3.75-6 3.75-6-3.75L18 2zM0 13.25l6-3.75 6 3.75L6 17 0 13.25zm18-3.75l6 3.75L18 17l-6-3.75 6-3.75zM6 18.25l6-3.75 6 3.75L12 22l-6-3.75z" />
                        </svg>
                      </button>
                      {isAdmin && (
                        <button
                          type="button"
                          onClick={() =>
                            setDiagTarget({
                              clientId: group.client.id,
                              clientName: group.client.name_salone || "Cliente",
                            })
                          }
                          data-testid={`prod-diagnostic-btn-${group.client?.id}`}
                          title="Diagnostica messaggi raw (debug video mancanti)"
                          className="h-8 w-8 rounded-full bg-amber-500 text-white grid place-items-center hover:bg-amber-600 transition-colors"
                        >
                          <Stethoscope className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
              {isExpanded && group.items.length === 0 && (
                <div
                  data-testid={`prod-empty-${cid}`}
                  className="rounded-2xl border border-dashed border-[var(--mp-border)] bg-[var(--mp-dash-bg)] p-5 text-center text-sm text-[var(--mp-muted)]"
                >
                  Nessun contenuto ancora arrivato da questo cliente. Usa
                  <strong> Carica video diretto </strong> qui sopra per
                  iniziare a lavorare, oppure aspetta che invii materiale
                  via Telegram.
                </div>
              )}
              {isExpanded && group.items.length > 0 && (() => {
                // Split items into subgroups: consecutive grouped items share
                // the same group_id; ungrouped items form their own buckets.
                const sub = [];
                for (const m of group.items) {
                  const gid = m.group_id || null;
                  const last = sub[sub.length - 1];
                  if (gid && last && last.gid === gid) {
                    last.items.push(m);
                  } else if (!gid && last && !last.gid) {
                    last.items.push(m);
                  } else {
                    sub.push({
                      gid,
                      name: m.group_name || null,
                      color: m.group_color || null,
                      editedAsset: m.edited_asset || null,
                      items: [m],
                    });
                  }
                }
                return sub.map((sg, idx) => {
                  if (!sg.gid) {
                    return (
                      <div key={`ung-${idx}`}>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 sm:gap-3">
                          {sg.items.map((m) => renderCard(m, group.client))}
                        </div>
                      </div>
                    );
                  }
                  // --- COMPACT GROUP CARD ---
                  const allEdited = sg.items.every((m) => m.task_edited);
                  const allPublished = sg.items.every((m) => m.task_published);
                  const allInProgress = sg.items.every((m) => m.task_in_progress);
                  const groupNote = sg.items.find((m) => m.task_note)?.task_note || "";
                  const first = sg.items[0];
                  const firstA = first?.attachment || {};
                  const hasAsset = !!sg.editedAsset;
                  const wid = `group-${sg.gid}`;
                  return (
                    <div
                      key={sg.gid}
                      data-testid={`prod-group-wrapper-${sg.gid}`}
                      className="rounded-2xl border bg-white p-3 sm:p-4 space-y-3"
                      style={{
                        borderColor: sg.color || "var(--mp-border)",
                        borderWidth: 2,
                      }}
                    >
                      {/* Group header */}
                      <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div className="flex items-center gap-2 min-w-0">
                          <div
                            className="h-8 w-8 rounded-full grid place-items-center text-white shrink-0"
                            style={{ backgroundColor: sg.color || "var(--mp-ink)" }}
                          >
                            <Layers className="h-4 w-4" />
                          </div>
                          <div className="min-w-0">
                            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[var(--mp-muted)] flex items-center gap-2 flex-wrap">
                              <span>Gruppo · {sg.items.length} clip</span>
                              {formatGroupCreatedAt(sg.items) && (
                                <span
                                  className="inline-flex items-center gap-1 normal-case tracking-normal text-[10px] text-[var(--mp-muted)]"
                                  data-testid={`prod-group-created-${sg.gid}`}
                                  title="Data e ora di creazione del gruppo"
                                >
                                  <Clock className="h-3 w-3" />
                                  {formatGroupCreatedAt(sg.items)}
                                </span>
                              )}
                            </div>
                            <div className="font-display text-base tracking-tight truncate flex items-center gap-1.5">
                              <span className="truncate">{sg.name}</span>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  renameGroup(sg.gid, sg.name);
                                }}
                                data-testid={`prod-group-rename-btn-${sg.gid}`}
                                title="Rinomina gruppo"
                                className="shrink-0 inline-flex items-center justify-center h-6 w-6 rounded-full text-[var(--mp-muted)] hover:bg-[var(--mp-ink)] hover:text-white transition-colors"
                              >
                                <Pencil className="h-3 w-3" />
                              </button>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <button
                            type="button"
                            onClick={() =>
                              openDropboxFolder(group.client?.id, "Lavorati")
                            }
                            data-testid={`prod-group-dropbox-lavorati-btn-${sg.gid}`}
                            title="Apri la cartella Lavorati di questo cliente su Dropbox"
                            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-[#0061FF] text-white text-xs font-medium hover:bg-[#0052D4] transition-colors"
                          >
                            <svg
                              viewBox="0 0 24 24"
                              className="h-3.5 w-3.5"
                              fill="currentColor"
                              aria-hidden="true"
                            >
                              <path d="M6 2l6 3.75L6 9.5 0 5.75 6 2zm12 0l6 3.75-6 3.75-6-3.75L18 2zM0 13.25l6-3.75 6 3.75L6 17 0 13.25zm18-3.75l6 3.75L18 17l-6-3.75 6-3.75zM6 18.25l6-3.75 6 3.75L12 22l-6-3.75z" />
                            </svg>
                            Lavorati
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              setManageGroup({
                                groupId: sg.gid,
                                groupName: sg.name,
                                clientId: group.client?.id,
                                items: sg.items,
                              })
                            }
                            data-testid={`prod-group-manage-btn-${sg.gid}`}
                            title="Aggiungi o rimuovi contenuti dal gruppo"
                            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full border border-[var(--mp-ink)] bg-white text-[var(--mp-ink)] text-xs font-medium hover:bg-[var(--mp-dash-bg)]"
                          >
                            <Settings2 className="h-3.5 w-3.5" /> Modifica gruppo
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              dissolveGroup(sg.gid, sg.name, sg.items.length)
                            }
                            disabled={workingId === sg.gid}
                            data-testid={`prod-group-dissolve-btn-${sg.gid}`}
                            title="Sciogli il gruppo: i contenuti tornano come card singole (i file NON vengono eliminati)"
                            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full border border-[var(--mp-ink)] bg-white text-[var(--mp-ink)] text-xs font-medium hover:bg-[var(--mp-dash-bg)] disabled:opacity-50 transition-colors"
                          >
                            <Unlink className="h-3.5 w-3.5" /> Sciogli gruppo
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              deleteGroup(sg.gid, sg.name, sg.items.length)
                            }
                            disabled={workingId === sg.gid}
                            data-testid={`prod-group-delete-btn-${sg.gid}`}
                            title="Elimina questo gruppo dalla produzione"
                            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full border border-[var(--mp-primary)] bg-white text-[var(--mp-primary)] text-xs font-medium hover:bg-[var(--mp-primary)]/10 disabled:opacity-50 transition-colors"
                          >
                            <Trash2 className="h-3.5 w-3.5" /> Elimina gruppo
                          </button>
                          <GroupZipButton
                            groupId={sg.gid}
                            itemCount={sg.items.length}
                            downloadableCount={
                              sg.items.filter(
                                (m) => m?.attachment?.dropbox_path,
                              ).length
                            }
                          />
                          <button
                            type="button"
                            onClick={() =>
                              setUploadTarget({
                                groupId: sg.gid,
                                fileName: sg.name,
                                clientName: group.client?.name_salone || "",
                                existing: sg.editedAsset,
                              })
                            }
                            data-testid={`prod-group-upload-btn-${sg.gid}`}
                            className={`inline-flex items-center gap-2 h-9 px-4 rounded-full border-2 text-sm font-medium transition-transform hover:-translate-y-0.5 ${
                              hasAsset
                                ? "bg-[var(--mp-ink)] border-[var(--mp-ink)] text-white"
                                : "bg-[var(--mp-yellow)] border-[var(--mp-ink)] text-[var(--mp-ink)] shadow-brutal-sm"
                            }`}
                          >
                            {hasAsset ? (
                              <CheckCircle2 className="h-4 w-4" />
                            ) : (
                              <Upload className="h-4 w-4" />
                            )}
                            {hasAsset ? "Lavorato caricato" : "Carica lavorato"}
                          </button>
                        </div>
                      </div>

                      {/* Thumbnail strip (compressed) */}
                      <div className="flex items-center gap-2 overflow-x-auto pb-1">
                        {sg.items.map((m) => {
                          const a = m.attachment || {};
                          const rel = a.dropbox_url || a.url;
                          const src = rel ? `${BACKEND_URL}${rel}` : null;
                          const fileName = a.display_name || a.file_name || a.kind || "file";
                          const isSel = !!selection[m.id];
                          // v1.40 — client-chosen sequence & label from the
                          // Magic Link picker. Only shown when set; empty
                          // for older uploads (backwards-compatible).
                          const seq = m.clip_sequence;
                          const label = (m.clip_label || "").trim();
                          return (
                            <div
                              key={m.id}
                              data-testid={`prod-group-thumb-${m.id}`}
                              className={`relative shrink-0 w-20 ${
                                label ? "" : ""
                              }`}
                              title={label ? `${label} · ${fileName}` : fileName}
                            >
                              <div
                                className={`relative h-20 w-20 rounded-lg overflow-hidden border ${
                                  isSel
                                    ? "border-[var(--mp-ink)] ring-2 ring-[var(--mp-ink)]"
                                    : "border-[var(--mp-border)]"
                                }`}
                              >
                              <a
                                href={src || "#"}
                                target="_blank"
                                rel="noreferrer"
                                onClick={(e) => !src && e.preventDefault()}
                                className="block h-full w-full"
                              >
                                <Thumb a={a} />
                              </a>
                              <label
                                className="absolute top-1 left-1 h-4 w-4 rounded bg-white/95 border border-[var(--mp-ink)] grid place-items-center cursor-pointer"
                                title={isSel ? "Deseleziona" : "Seleziona"}
                              >
                                <input
                                  type="checkbox"
                                  checked={isSel}
                                  onChange={() => toggleSelect(m)}
                                  data-testid={`prod-select-${m.id}`}
                                  className="h-3 w-3 accent-[var(--mp-ink)]"
                                />
                              </label>
                              <button
                                type="button"
                                onClick={() => deleteCard(m, fileName)}
                                disabled={workingId === m.id}
                                data-testid={`prod-group-delete-${m.id}`}
                                title="Elimina contenuto"
                                className="absolute top-1 right-1 h-5 w-5 rounded-full bg-white/95 border border-[var(--mp-border)] grid place-items-center text-red-600 hover:bg-red-50 hover:border-red-300 disabled:opacity-50"
                              >
                                <Trash2 className="h-2.5 w-2.5" />
                              </button>
                              {a.kind === "video" && (
                                <div className="absolute inset-0 grid place-items-center pointer-events-none">
                                  <div className="h-6 w-6 rounded-full bg-white/90 grid place-items-center border border-[var(--mp-ink)]">
                                    <Video className="h-2.5 w-2.5" />
                                  </div>
                                </div>
                              )}
                              {m.source === "upload_link" && (
                                <div
                                  data-testid={`prod-badge-via-web-${m.id}`}
                                  title="Caricato dal cliente via portale web (magic link) — video >20 MB non passa da Telegram"
                                  className="absolute bottom-0.5 left-0.5 px-1 py-0.5 rounded-md bg-[var(--mp-ink)] text-white text-[9px] font-bold flex items-center gap-0.5 pointer-events-none shadow-sm"
                                >
                                  <Smartphone className="h-2 w-2" />
                                  <span className="leading-none">Web</span>
                                </div>
                              )}
                              {seq != null && (
                                <div
                                  data-testid={`prod-clip-seq-${m.id}`}
                                  title={`Ordine scelto dal cliente: ${seq}`}
                                  className="absolute bottom-0.5 right-0.5 h-5 min-w-[20px] px-1 rounded-full bg-[var(--mp-yellow)] border border-[var(--mp-ink)] text-[var(--mp-ink)] text-[10px] font-bold grid place-items-center pointer-events-none shadow-sm"
                                >
                                  {String(seq).padStart(2, "0")}
                                </div>
                              )}
                              </div>
                              {label && (
                                <div
                                  data-testid={`prod-clip-label-${m.id}`}
                                  title={label}
                                  className="mt-1 text-[10px] leading-tight font-semibold text-[var(--mp-ink)] truncate"
                                >
                                  {label}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>

                      {/* Unified group action buttons */}
                      <div className="flex flex-wrap gap-1.5 pt-1">
                        <MiniToggle
                          checked={allInProgress}
                          onClick={() => flagGroup(sg.gid, "in_progress", !allInProgress)}
                          loading={workingId === wid}
                          tone="blue"
                          icon={<Clock className="h-3 w-3 shrink-0" />}
                          label="In lavorazione"
                          title={allInProgress ? "Togli stato in lavorazione" : "Segna in lavorazione"}
                          testId={`prod-group-toggle-inprogress-${sg.gid}`}
                        />
                        <MiniToggle
                          checked={allEdited}
                          onClick={() => flagGroup(sg.gid, "edited", !allEdited)}
                          loading={workingId === wid}
                          tone="yellow"
                          icon={<Hammer className="h-3 w-3 shrink-0" />}
                          label="Da montare a mano"
                          title={allEdited ? "Togli stato da montare a mano" : "Segna da montare a mano"}
                          testId={`prod-group-toggle-edited-${sg.gid}`}
                        />
                        <MiniToggle
                          checked={allPublished}
                          onClick={() => flagGroup(sg.gid, "published", !allPublished)}
                          loading={workingId === wid}
                          tone="ink"
                          icon={<Megaphone className="h-3 w-3 shrink-0" />}
                          label="Pubblicato"
                          title={allPublished ? "Togli stato pubblicato" : "Segna pubblicato"}
                          testId={`prod-group-toggle-published-${sg.gid}`}
                        />
                        <button
                          type="button"
                          onClick={() =>
                            setNoteEditing({
                              groupId: sg.gid,
                              note: groupNote,
                              clientName: group.client?.name_salone || "",
                              fileName: sg.name,
                            })
                          }
                          data-testid={`prod-group-note-btn-${sg.gid}`}
                          title={groupNote ? "Modifica nota del gruppo" : "Aggiungi nota al gruppo"}
                          className={`flex-1 h-7 px-2 rounded-full border inline-flex items-center justify-center gap-1 transition-colors text-[11px] font-medium whitespace-nowrap ${
                            groupNote
                              ? "bg-[var(--mp-yellow)] border-[var(--mp-ink)] text-[var(--mp-ink)]"
                              : "bg-white border-[var(--mp-border)] text-[var(--mp-muted)] hover:text-[var(--mp-ink)] hover:border-[var(--mp-ink)]"
                          }`}
                        >
                          <Pencil className="h-3 w-3 shrink-0" />
                          <span>Nota</span>
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            setPreparing({
                              clientId: group.client?.id,
                              clientName: group.client?.name_salone || "",
                              mediaUrl:
                                sg.editedAsset?.dropbox_url ||
                                sg.editedAsset?.url ||
                                firstA.dropbox_url ||
                                firstA.url ||
                                "",
                              mediaKind: firstA.kind === "photo" ? "foto" : "video",
                              sourceMessageId: first?.id,
                            })
                          }
                          data-testid={`prod-group-prep-btn-${sg.gid}`}
                          title="Programma post (apri dialog AI + Coda Metricool)"
                          className="flex-1 h-7 px-2 rounded-full border border-[var(--mp-ink)] bg-[var(--mp-yellow)] text-[var(--mp-ink)] inline-flex items-center justify-center gap-1 hover:bg-[var(--mp-ink)] hover:text-white transition-colors text-[11px] font-medium whitespace-nowrap"
                        >
                          <Sparkles className="h-3 w-3 shrink-0" />
                          <span>Programma</span>
                        </button>
                      </div>

                      {groupNote && (
                        <div
                          data-testid={`prod-group-note-preview-${sg.gid}`}
                          className="text-[11px] text-[var(--mp-muted)] italic whitespace-pre-wrap border-t border-[var(--mp-border)] pt-2"
                        >
                          📌 {groupNote}
                        </div>
                      )}
                    </div>
                  );
                });
              })()}
            </section>
            );
          })}
        </div>
      )}

      <NoteDialog
        editing={noteEditing}
        onClose={() => setNoteEditing(null)}
        onSave={saveNote}
        onSaveGroup={saveGroupNote}
      />

      <ManageGroupDialog
        open={!!manageGroup}
        onOpenChange={(v) => !v && setManageGroup(null)}
        groupId={manageGroup?.groupId}
        groupName={manageGroup?.groupName}
        clientId={manageGroup?.clientId}
        currentItems={manageGroup?.items}
        onSaved={() => {
          setManageGroup(null);
          load();
        }}
      />

      <PreparePostDialog
        open={!!preparing}
        onOpenChange={(v) => !v && setPreparing(null)}
        clientId={preparing?.clientId}
        clientName={preparing?.clientName}
        mediaUrl={preparing?.mediaUrl}
        mediaKind={preparing?.mediaKind}
        sourceMessageId={preparing?.sourceMessageId}
        onCreated={() => {
          setPreparing(null);
          toast.success("Post in coda. Vai in 'Coda Metricool' per pubblicarlo.");
        }}
      />

      <GroupAssignDialog
        open={!!groupDialog}
        onOpenChange={(v) => !v && setGroupDialog(null)}
        messageIds={groupDialog?.messageIds || []}
        existingName={groupDialog?.existingName}
        existingColor={groupDialog?.existingColor}
        onSaved={() => {
          clearSelection();
          load();
        }}
      />

      <EditedUploadDialog
        open={!!uploadTarget}
        onOpenChange={(v) => !v && setUploadTarget(null)}
        messageId={uploadTarget?.messageId}
        groupId={uploadTarget?.groupId}
        fileName={uploadTarget?.fileName}
        clientName={uploadTarget?.clientName}
        existing={uploadTarget?.existing}
        onUpdated={() => {
          setUploadTarget(null);
          load();
        }}
      />

      <ClientDiagnosticDialog
        open={!!diagTarget}
        onOpenChange={(v) => !v && setDiagTarget(null)}
        clientId={diagTarget?.clientId}
        clientName={diagTarget?.clientName}
      />
    </div>
  );
}
